import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiBriefcase,
  FiChevronDown,
  FiColumns,
  FiFileText,
  FiInfo,
  FiMessageSquare,
  FiPrinter,
  FiRotateCcw,
  FiSearch,
  FiSliders,
  FiUser,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

import { addMember } from "../ApiMember";
import SearchReport from "./SearchReport";
import ReportTable from "./ReportTable";
import ColumnPicker from "./ColumnPicker";
import PrintReportModal from "./PrintReportModal";
import SendSMSModal from "../../SMSMessage/SendSMS";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetSelectedLanguage } from "../../helper/Method";
import useSorting from "../../shared/useSorting";
import { IconInput } from "./ReportControls";
import {
  FilterChips,
  buildAppliedFilters,
  createDefaultSearchModel,
  removeAppliedFilter,
  toSearchPayload,
} from "./reportFilters";
import {
  getDefaultColumns,
  loadRememberedColumns,
  saveRememberedColumns,
  toColumnModel,
} from "./reportColumns";
import "./MemberReport.css";

const initialValues = {
  name: "neutral",
  dateOfRegistration: "neutral",
  registrationNumber: "neutral",
  memberType: "neutral",
  responsbilities: "neutral",
};

const SEARCHABLE_FIELDS = ["name", "fatherName", "registrationNumber", "emailAndPhoneNumber", "nazam"];

const localDate = (separator = "/") => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join(separator);
};

const ReportList = () => {
  const { t } = useTranslation();
  const selectedLanguage = GetSelectedLanguage();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const memberId = parseInt(encryptStorage.getItem("memberId"));
  const defaultModel = useMemo(() => createDefaultSearchModel(loggedInUnitId), [loggedInUnitId]);

  const [reportSearchMemberModel, setReportSearchMemberModel] = useState(defaultModel);
  const [searchButton, setSearchButton] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [columns, setColumns] = useState(() => loadRememberedColumns() || getDefaultColumns());
  const [rememberColumns, setRememberColumns] = useState(() => loadRememberedColumns() !== null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printJob, setPrintJob] = useState(null);
  const [smsModal, setSmsModal] = useState(false);
  const [memberSMSStatus, setMemberSMSStatus] = useState(false);
  const { sortObject, handleSorting } = useSorting(initialValues);

  useEffect(() => {
    if (!searchButton) return;
    let cancelled = false;
    setLoading(true);
    const sortModel = {
      isFullNameDescending: sortObject.name === "descending",
      isFullNameAscending: sortObject.name === "ascending",
      isJoiningDateDescending: sortObject.dateOfRegistration === "descending",
      isJoiningDateAscending: sortObject.dateOfRegistration === "ascending",
      isRegNoAscending: sortObject.registrationNumber === "ascending",
      isRegNoDescending: sortObject.registrationNumber === "descending",
      isMemberTypeAscending: sortObject.memberType === "ascending",
      isMemberTypeDescending: sortObject.memberType === "descending",
      isResponsbilitiesAscending: sortObject.responsbilities === "ascending",
      isResponsbilitiesDescending: sortObject.responsbilities === "descending",
    };

    addMember()
      .getMembersReport(toSearchPayload(reportSearchMemberModel, sortModel))
      .then((res) => {
        if (cancelled) return;
        setMembers(res.data);
        setSelectedIds(new Set());
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        console.log(err);
      });
    return () => {
      cancelled = true;
    };
  }, [reportSearchMemberModel, sortObject, searchButton]);

  useEffect(() => {
    addMember()
      .checkMemberCanSendSMS(memberId)
      .then((res) => setMemberSMSStatus(res.data))
      .catch((err) => console.log(err));
  }, [memberId]);

  useEffect(() => {
    if (!printJob) return;
    window.print();
    setPrintJob(null);
  }, [printJob]);

  const appliedFilters = buildAppliedFilters(reportSearchMemberModel);
  const filterSummary = appliedFilters
    .map((c) => (c.text ? t(c.text) : `${t(c.label)}: ${c.value}`))
    .join(" · ");

  const displayRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!members || !q) return members || [];
    return members.filter((m) => SEARCHABLE_FIELDS.some((f) => m[f] && String(m[f]).toLowerCase().includes(q)));
  }, [members, query]);

  const selectedRows = useMemo(() => (members || []).filter((m) => selectedIds.has(m.id)), [members, selectedIds]);
  const printRows = printJob && printJob.selectedOnly ? selectedRows : displayRows;

  const kpis = useMemo(() => {
    const list = members || [];
    const counts = {};
    list.forEach((m) => m.memberType && (counts[m.memberType] = (counts[m.memberType] || 0) + 1));
    return {
      total: list.length,
      topTypes: Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2),
      responsible: list.filter((m) => m.responsbilities && m.responsbilities.trim() !== "").length,
    };
  }, [members]);

  const handleSubmit = (model) => {
    setReportSearchMemberModel(model);
    setSearchButton(true);
    setFiltersOpen(false);
  };

  const handleRemoveFilter = (key) => setReportSearchMemberModel((m) => removeAppliedFilter(m, key));

  const handleResetFilters = () => setReportSearchMemberModel({ ...defaultModel, ui: { ...defaultModel.ui } });

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = (on) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      displayRows.forEach((r) => (on ? next.add(r.id) : next.delete(r.id)));
      return next;
    });

  const handleApplyColumns = (selected, remember) => {
    setColumns(selected);
    setRememberColumns(remember);
    saveRememberedColumns(selected, remember);
    setColumnsOpen(false);
  };

  const exportExcel = (rows) => {
    setExporting(true);
    const fileName = `Rufqa-Report_${localDate("-")}.xlsx`;
    addMember()
      .generateMembersReport({ members: rows, columnModel: toColumnModel(columns) })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, fileName);
        toast.success(`${t("member_report_export_done")} · ${fileName}`);
      })
      .catch((err) => {
        console.log(err);
        toast.error(t("member_report_export_failed"));
      })
      .finally(() => setExporting(false));
  };

  const handlePrint = (job) => {
    setPrintOpen(false);
    setPrintJob({ ...job, printedOn: localDate() });
  };

  const canSendSms = memberId !== 0 && memberSMSStatus;
  const smsList = selectedRows.map((m) => ({ status: true, mobileNo: m.emailAndPhoneNumber }));
  const hasResults = members !== null;
  const unitName = reportSearchMemberModel.ui.unitName;

  const kpiTile = (key, icon, tone, label, value, pct, barColor, labelIsUrdu = false) => (
    <div className="mr-kpi" key={key}>
      <span className={"mr-icon-tile mr-tone-" + tone}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <small className={labelIsUrdu ? "ur" : ""}>{label}</small>
        <b className="en">
          {value}
          {pct !== null && <small>{pct}%</small>}
        </b>
        <div className="mr-bar">
          <i style={{ width: (pct === null ? 100 : pct) + "%", background: barColor }} />
        </div>
      </div>
    </div>
  );

  const percent = (n) => (kpis.total ? Math.round((n * 100) / kpis.total) : 0);

  return (
    <div className="mr-page wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        closeOnClick
        bodyClassName={selectedLanguage}
        pauseOnHover={false}
        rtl={document.body.dir === "rtl"}
      />
      {smsModal && <SendSMSModal setModal={setSmsModal} memberList={smsList} memberListLength={smsList.length} />}
      <ColumnPicker
        isOpen={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        selected={columns}
        remember={rememberColumns}
        onApply={handleApplyColumns}
      />
      <PrintReportModal
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        onPrint={handlePrint}
        defaultTitle={t("member_report_page_title") + (unitName ? " — " + unitName : "")}
        filterSummary={filterSummary}
        columnCount={columns.length}
        selectedCount={selectedRows.length}
        totalCount={(members || []).length}
      />

      <div className="mr-page-head noprint">
        <span className="mr-page-icon">
          <FiUsers size={24} />
        </span>
        <h1>{t("member_report_page_title")}</h1>
      </div>

      {filtersOpen ? (
        <div className="noprint">
          <SearchReport model={reportSearchMemberModel} defaultModel={defaultModel} onSubmit={handleSubmit} />
        </div>
      ) : (
        <div className="mr-panel noprint">
          <div className="mr-applied">
            <span className="mr-lbl">{t("member_report_filters")}</span>
            <FilterChips chips={appliedFilters} onRemove={handleRemoveFilter} />
            <div className="mr-push">
              <button type="button" className="mr-btn mr-btn-ghost mr-sm" onClick={() => setFiltersOpen(true)}>
                <FiSliders size={14} /> {t("member_report_edit_filters")}
              </button>
              <button type="button" className="mr-btn mr-btn-soft mr-sm" onClick={handleResetFilters}>
                <FiRotateCcw size={14} /> {t("member_report_reset")}
              </button>
            </div>
          </div>
        </div>
      )}

      {searchButton && !hasResults && loading && (
        <div className="mr-panel noprint">
          <div className="mr-loading-line" />
          <div className="mr-toolbar">
            <span className="mr-meta">{t("member_report_building")}</span>
          </div>
          <div className="mr-tbl-wrap">
            <table className="mr-table">
              <tbody>
                {[70, 90, 60, 85, 75, 65].map((w, i) => (
                  <tr key={i}>
                    {[w + 40, 56, w, 90, 70, w * 2].map((cw, j) => (
                      <td key={j}>
                        <div className="mr-skel" style={{ width: cw }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasResults && members.length === 0 && (
        <div className="mr-panel noprint">
          {loading && <div className="mr-loading-line" />}
          <div className="mr-empty">
            <div className="mr-empty-art">
              <FiSearch size={38} />
            </div>
            <h3>{t("member_report_empty_title")}</h3>
            <p>{t("member_report_empty_desc")}</p>
            <div className="mr-acts">
              <FilterChips chips={appliedFilters} onRemove={handleRemoveFilter} />
            </div>
            <div className="mr-acts">
              <button type="button" className="mr-btn mr-btn-ghost" onClick={() => setFiltersOpen(true)}>
                <FiSliders size={14} /> {t("member_report_edit_filters")}
              </button>
              <button type="button" className="mr-btn mr-btn-primary" onClick={handleResetFilters}>
                <FiRotateCcw size={14} /> {t("member_report_reset_filters")}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasResults && members.length > 0 && (
        <>
          <div className="mr-kpis noprint">
            {kpiTile("total", <FiUsers size={19} />, "teal", t("member_report_kpi_total"), kpis.total, null, "linear-gradient(135deg, #0d9488, #4f46e5)")}
            {kpis.topTypes.map(([type, count], i) =>
              kpiTile(
                type,
                i === 0 ? <FiUserCheck size={19} /> : <FiUser size={19} />,
                i === 0 ? "teal" : "sky",
                type,
                count,
                percent(count),
                i === 0 ? "#0d9488" : "#0284c7",
                true
              )
            )}
            {kpiTile(
              "responsible",
              <FiBriefcase size={19} />,
              "indigo",
              t("member_report_kpi_responsible"),
              kpis.responsible,
              percent(kpis.responsible),
              "#4f46e5"
            )}
          </div>

          <div className="mr-panel" id="section-to-print">
            {loading && <div className="mr-loading-line noprint" />}
            {printJob && (
              <div className="print mr-print-head">
                <h3 className={selectedLanguage}>{printJob.title}</h3>
                <p>
                  {t("member_report_printed_on")} <span className="en">{printJob.printedOn}</span> ·{" "}
                  {t("member_report_count", { value: printRows.length })}
                </p>
                {printJob.showFilters && filterSummary && <p>{filterSummary}</p>}
              </div>
            )}

            <div className="mr-toolbar noprint">
              <IconInput icon={<FiSearch size={15} />}>
                <Input
                  value={query}
                  placeholder={t("member_report_search_results")}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </IconInput>
              <span className="mr-meta">
                {query.trim()
                  ? t("member_report_showing_filtered", { shown: displayRows.length, total: members.length })
                  : t("member_report_count", { value: members.length })}
              </span>
              <button type="button" className="mr-btn mr-btn-ghost mr-sm" onClick={() => setColumnsOpen(true)}>
                <FiColumns size={14} /> {t("member_report_columns")} <span className="mr-badge">{columns.length}</span>
              </button>
              <button type="button" className="mr-btn mr-btn-ghost mr-sm" onClick={() => setPrintOpen(true)}>
                <FiPrinter size={14} /> {t("common_print")}
              </button>
              <Dropdown isOpen={exportMenuOpen} toggle={() => setExportMenuOpen((o) => !o)} className="mr-split">
                <button
                  type="button"
                  className="mr-btn mr-btn-excel mr-sm"
                  disabled={exporting}
                  onClick={() => exportExcel(members)}
                >
                  {exporting ? <span className="mr-spin" /> : <FiFileText size={14} />} {t("member_report_export_excel")}
                </button>
                <DropdownToggle
                  tag="button"
                  type="button"
                  className="mr-btn mr-btn-excel mr-sm"
                  disabled={exporting}
                  aria-label={t("member_report_export_options")}
                >
                  <FiChevronDown size={14} />
                </DropdownToggle>
                <DropdownMenu end className="mr-menu">
                  <DropdownItem onClick={() => exportExcel(members)}>
                    <span className="mr-icon-tile mr-tone-teal">
                      <FiFileText size={16} />
                    </span>
                    <span>
                      <b>{t("member_report_export_all")}</b>
                      <small>{t("member_report_rows_columns", { rows: members.length, cols: columns.length })}</small>
                    </span>
                  </DropdownItem>
                  <DropdownItem disabled={selectedRows.length === 0} onClick={() => exportExcel(selectedRows)}>
                    <span className="mr-icon-tile mr-tone-indigo">
                      <FiUserCheck size={16} />
                    </span>
                    <span>
                      <b>{t("member_report_export_selected")}</b>
                      <small>
                        {selectedRows.length === 0
                          ? t("member_report_export_selected_hint")
                          : t("member_report_rows_columns", { rows: selectedRows.length, cols: columns.length })}
                      </small>
                    </span>
                  </DropdownItem>
                  <div className="mr-menu-foot">{t("member_report_export_note")}</div>
                </DropdownMenu>
              </Dropdown>
            </div>

            {selectedRows.length > 0 && (
              <div className="mr-selbar noprint">
                <span>{t("member_report_selected", { value: selectedRows.length })}</span>
                <div className="mr-push">
                  {canSendSms && (
                    <button type="button" className="mr-btn mr-btn-sms mr-sm" onClick={() => setSmsModal(true)}>
                      <FiMessageSquare size={14} /> {t("sms_send_sms")}{" "}
                      <span className="mr-badge">{selectedRows.length}</span>
                    </button>
                  )}
                  <button type="button" className="mr-btn mr-btn-ghost mr-sm" onClick={() => setSelectedIds(new Set())}>
                    {t("member_report_clear_selection")}
                  </button>
                </div>
              </div>
            )}

            {displayRows.length > 0 ? (
              <ReportTable
                rows={printJob ? printRows : displayRows}
                columns={columns}
                sortObject={sortObject}
                onSort={handleSorting}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
              />
            ) : (
              <div className="mr-empty">
                <p>{t("member_report_no_search_match")}</p>
              </div>
            )}

            <div className="mr-tbl-foot noprint">
              <span>
                {query.trim()
                  ? t("member_report_showing_filtered", { shown: displayRows.length, total: members.length })
                  : t("member_report_showing_all", { value: members.length })}
              </span>
              {columns.includes("name") && (
                <span className="mr-push">
                  <FiInfo size={13} /> {t("member_report_pinned_hint")}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportList;
